/**
 * WebSocket hook for real-time dashboard events — Phase 5.
 *
 * Connects to ws://host/ws/dashboard?token=<jwt>
 * Company context is derived from the JWT (server-side) — not client-supplied.
 * Auto-reconnects with exponential backoff on disconnect.
 * Returns the latest event and connection status.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface WebSocketEvent {
  type: string;
  company_id?: string;
  timestamp?: string;
  [key: string]: unknown;
}

interface UseWebSocketOptions {
  companyId?: string;
  enabled?: boolean;
  maxReconnectAttempts?: number;
}

interface UseWebSocketReturn {
  lastEvent: WebSocketEvent | null;
  isConnected: boolean;
  sendMessage: (msg: string) => void;
}

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { companyId: _companyId, enabled = true, maxReconnectAttempts = 5 } = options;
  const [lastEvent, setLastEvent] = useState<WebSocketEvent | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const connect = useCallback(() => {
    if (!enabled) return;

    // httpOnly cookies are sent automatically by the browser on same-origin
    // WebSocket connections — no manual token extraction needed.
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    // company_id is extracted from the JWT server-side — not sent as a param
    const url = `${protocol}//${host}/ws/dashboard`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as WebSocketEvent;
          if (data.type !== 'pong') {
            setLastEvent(data);
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;

        // Auto-reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current += 1;
          reconnectTimer.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      // Connection failed
    }
  }, [enabled, maxReconnectAttempts]);

  const sendMessage = useCallback((msg: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(msg);
    }
  }, []);

  // Heartbeat ping every 30 seconds
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      sendMessage('ping');
    }, 30000);

    return () => clearInterval(interval);
  }, [isConnected, sendMessage]);

  // Connect/disconnect lifecycle
  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return { lastEvent, isConnected, sendMessage };
}
